import * as _ from "lodash";

export type FilterBasicDataType = string | number | Date;
export type FilterBasicDataTypeArray = string[] | number[] | Date[];
export type FilterConditionOperation =
    | { inq: FilterBasicDataTypeArray }
    | { in: FilterBasicDataTypeArray }
    | { nin: FilterBasicDataTypeArray }
    | { notIn: FilterBasicDataTypeArray }
    | { like: string }
    | { nlike: string }
    | { ilike: string }
    | { nilike: string }
    | { contains: string }
    | { notContains: string }
    | { gte: FilterBasicDataType }
    | { ">=": FilterBasicDataType }
    | { gt: FilterBasicDataType }
    | { ">": FilterBasicDataType }
    | { lte: FilterBasicDataType }
    | { "<="?: FilterBasicDataType }
    | { lt: FilterBasicDataType }
    | { "<": FilterBasicDataType }
    | { neq: FilterBasicDataType }
    | { "!=": FilterBasicDataType }
    | { "<>": FilterBasicDataType };

export type FilterCondition =
    | { [symbolId: string]: FilterBasicDataType | FilterConditionOperation }
    | { and: FilterCondition[] }
    | { or: FilterCondition[] };

const dctOpArgTypes: { [opName: string]: "string" | "BasicDataType" | "BasicDataTypeArray" | "FilterConditionArray" } =
    {
        inq: "BasicDataTypeArray",
        in: "BasicDataTypeArray",
        nin: "BasicDataTypeArray",
        notIn: "BasicDataTypeArray",
        like: "string",
        nlike: "string",
        ilike: "string",
        nilike: "string",
        contains: "string",
        notContains: "string",
        gte: "BasicDataType",
        ">=": "BasicDataType",
        gt: "BasicDataType",
        ">": "BasicDataType",
        lte: "BasicDataType",
        "<=": "BasicDataType",
        lt: "BasicDataType",
        "<": "BasicDataType",
        neq: "BasicDataType",
        "!=": "BasicDataType",
        "<>": "BasicDataType",
        and: "FilterConditionArray",
        or: "FilterConditionArray",
    };

export class FilterUtils {
    // Example conditions
    // Ex1:    {and: [ {symbol1: 10, symbol2: {operator: argument} ]}
    // Ex2:    {symbol2: {operator: argument}}
    // Ex3:    {symbol3: basicType }

    private static parse(
        cnd: FilterCondition,
        onSymbol?: (symbolName: string, arg: any) => boolean | void,
        onSymbolObject?: (symbolObj: any) => boolean | void,
        onOperator?: (operatorName: string, arg: any) => boolean | void,
        onError?: (error: string, arg: any) => boolean | void,
    ): void {
        // Filter Conditions can start with:
        // "and" operator, "or" operator, or a symbolObject
        const opOrFalse = FilterUtils.isOperatorObject(cnd);
        switch (opOrFalse) {
            case "and":
            case "or": {
                const arg = (<any>cnd)[opOrFalse];
                if (_.isFunction(onOperator)) {
                    const bStopOnOperator = onOperator(opOrFalse, arg);
                    if (bStopOnOperator) return;
                }
                if (!_.isArray(arg)) {
                    if (_.isFunction(onError)) {
                        const bStopOnError = onError(`Operator "${opOrFalse}" has non Array argument `, cnd);
                        if (bStopOnError) return;
                    }
                } else {
                    // Parse each item
                    for (const aChildCnd of arg) {
                        FilterUtils.parse(aChildCnd, onSymbol, onSymbolObject, onOperator, onError);
                    }
                }
                break;
            }
            case false: {
                if (FilterUtils.isSymbolObject(cnd)) {
                    if (_.isFunction(onSymbolObject)) {
                        const bStopOnSymbolObject = onSymbolObject(cnd);
                        if (bStopOnSymbolObject) return;
                    }

                    // Iterate Over Symbols & Validate
                    const arrSymbols = _.keys(cnd);
                    for (const aSymbol of arrSymbols) {
                        const arg = (<any>cnd)[aSymbol];
                        if (_.isFunction(onSymbol)) {
                            const bStopOnSymbol = onSymbol(aSymbol, arg);
                            if (bStopOnSymbol) return;
                        }

                        // Validate Symbol Arg
                        if (FilterUtils.isBasicType(arg)) continue; // Good Operator, could do type checking, but don't yet

                        // Validate Operator, any Operator other than and/or
                        const opOrFalse = FilterUtils.isOperatorObject(arg);
                        switch (opOrFalse) {
                            case "and":
                            case "or":
                            case false: {
                                if (_.isFunction(onError)) {
                                    const bStopOnError = onError(
                                        `Operator Argument "${opOrFalse}" has not recognized or invalid`,
                                        arg,
                                    );
                                    if (bStopOnError) return;
                                }
                                break;
                            }
                            default: {
                                const argExpectedType = dctOpArgTypes[opOrFalse];
                                const symbolOpArg = arg[opOrFalse];
                                let bLogError = false;
                                let sErrorMsg = "";

                                switch (argExpectedType) {
                                    case "BasicDataType":
                                        if (!FilterUtils.isBasicType(symbolOpArg)) {
                                            bLogError = true;
                                            sErrorMsg = `Operator ${opOrFalse} expects Basic Data Type`;
                                        }
                                        break;
                                    case "BasicDataTypeArray":
                                        if (!_.isArray(symbolOpArg)) {
                                            bLogError = true;
                                            sErrorMsg = `Operator ${opOrFalse} expects Array`;
                                        } else {
                                            // Check Elements
                                            const invalidElements: any[] = _.filter(
                                                symbolOpArg,
                                                (element) => !FilterUtils.isBasicType(element),
                                            );
                                            if (invalidElements.length) {
                                                bLogError = true;
                                                sErrorMsg = `Operator ${opOrFalse} has Array with ${invalidElements.length} invalid elements`;
                                            }
                                        }
                                        break;
                                    case "string":
                                        if (!_.isString(symbolOpArg)) {
                                            bLogError = true;
                                            sErrorMsg = `Operator ${opOrFalse} expects String`;
                                        }
                                        break;
                                }

                                if (_.isFunction(onError) && bLogError) {
                                    const bStopOnError = onError(sErrorMsg, symbolOpArg);
                                    if (bStopOnError) return;
                                }
                            }
                        }
                    } // Next Symbol
                } else {
                    if (_.isFunction(onError)) {
                        const bStopOnError = onError("Unrecognized Condition", cnd);
                        if (bStopOnError) return;
                    }
                }
                break;
            }
            default:
                if (_.isFunction(onError)) {
                    const bStopOnError = onError("Unrecognized Condition", cnd);
                    if (bStopOnError) return;
                }
        }
    }

    static getSymbolReferences(cnd: FilterCondition): string[] {
        const dctSymbols: { [id: string]: string } = {};

        FilterUtils.parse(cnd, (s, a) => {
            if (!(s in dctSymbols)) dctSymbols[s] = s;
        });

        return _.values(dctSymbols);
    }

    static getErrors(cnd: FilterCondition, limit?: number): { error: string; arg: any }[] {
        let rv: { error: string; arg: any }[] = [];

        const bLimit = limit > 0;

        FilterUtils.parse(cnd, undefined, undefined, undefined, (str, ar) => {
            rv.push({ error: str, arg: ar });
            if (bLimit && rv.length >= limit) return false;
        });
        return rv;
    }

    static changeSymbols(cnd: FilterCondition, dct: { [srcSymbolId: string]: string }): FilterCondition {
        let sErrors: string[] = [];
        const rv: FilterCondition = {};

        function convertSymbols(src: FilterCondition, tgt: FilterCondition) {
            // Filter Conditions can start with:
            // "and" operator, "or" operator, or a symbolObject
            const opOrFalse = FilterUtils.isOperatorObject(src);
            switch (opOrFalse) {
                case "and":
                case "or": {
                    let newArgs: any[] = ((<any>tgt)[opOrFalse] = []);
                    const arg = (<any>src)[opOrFalse];
                    if (!_.isArray(arg)) {
                        sErrors.push(`Expected Array for operator ${opOrFalse}`);
                        return;
                    } else {
                        // Parse each item
                        for (const aChildCnd of arg) {
                            const newChild: FilterCondition = {};
                            convertSymbols(aChildCnd, newChild);
                            newArgs.push(newChild);
                        }
                    }
                    break;
                }
                case false: {
                    if (FilterUtils.isSymbolObject(src)) {
                        // Iterate Over Symbols & Validate
                        const arrSymbols = _.keys(src);
                        for (const aSymbol of arrSymbols) {
                            const arg = (<any>src)[aSymbol];
                            let tgtSymbol = aSymbol;
                            if (aSymbol in dct) tgtSymbol = dct[aSymbol];

                            (<any>tgt)[tgtSymbol] = arg;
                        } // Next Symbol
                    } else {
                        sErrors.push("Unrecognized Condition");
                        return;
                    }
                    break;
                }
                default:
                    sErrors.push("Unrecognized Condition");
                    return;
            }
        }

        convertSymbols(cnd, rv);

        if (sErrors.length) return undefined;

        return rv;
    }

    private static isBasicType(obj: any): string | boolean {
        return _.isNumber(obj) || _.isDate(obj) || _.isString(obj);
    }

    private static isOperatorObject(obj: any): string | false {
        if (!_.isObjectLike(obj)) return false;

        if (_.isArray(obj)) return false;

        // Check The Key
        const keys = _.keys(obj);
        if (keys.length != 1) return false;
        if (keys[0] in dctOpArgTypes) return keys[0];

        return false;
    }

    private static isSymbolObject(obj: any): boolean {
        if (!_.isObjectLike(obj)) return false;
        if (_.isArray(obj)) return false;

        // Check The Key, They should not be an operator
        const keys = _.keys(obj);

        for (const key of keys) {
            if (key in dctOpArgTypes) return false;
        }

        return true;
    }

    static createFunction<Type>(
        cnd: FilterCondition,
        dctSymbolType: { [symbolId: string]: "number" | "string" | "date" },
        symbolAccess: (symbolId: string, obj: Type) => any,
    ): (obj: Type) => boolean {
        //--
        if (_.isEmpty(cnd)) return () => true;

        const opOrFalse = FilterUtils.isOperatorObject(cnd);
        switch (opOrFalse) {
            case "and":
            case "or": {
                let bIsAnd = opOrFalse == "and";
                let bIsOr = opOrFalse == "or";

                const arg = (<any>cnd)[opOrFalse];
                if (!_.isArray(arg)) {
                    // Expected Array
                    return () => false;
                } else {
                    const subFuncs = _.map(arg, (itemCnd) =>
                        FilterUtils.createFunction(itemCnd, dctSymbolType, symbolAccess),
                    );
                    const notFuncs = _.remove(subFuncs, (f) => !_.isFunction(f));
                    return (obj: any) => {
                        // Check Each Function
                        for (let fnc of subFuncs) {
                            const bResSub = fnc(obj);
                            if (bIsOr && bResSub) return true;
                            if (bIsAnd && !bResSub) return false;
                        }
                        if (bIsOr) return false;

                        return true;
                    };
                }
                break;
            }
            case false: {
                if (FilterUtils.isSymbolObject(cnd)) {
                    // Iterate Over Symbols & Validate
                    const arrSymbols = _.keys(cnd);

                    const objFuncs: any[] = [];
                    for (const aSymbolId of arrSymbols) {
                        const arg = (<any>cnd)[aSymbolId];
                        const aFunc = FilterUtils.createOperatorFunction(
                            arg,
                            aSymbolId,
                            dctSymbolType[aSymbolId],
                            symbolAccess,
                        );
                        objFuncs.push(aFunc);
                    } // Next Symbol

                    const removedFuncs = _.remove(objFuncs, (fnc) => !_.isFunction(fnc));

                    return (obj: any) => {
                        // return false if any of functions return false
                        for (const aFunc of objFuncs) {
                            if (!aFunc(obj)) return false;
                        }

                        return true;
                    };
                } else {
                    // Unrecognized Condition
                    return () => false;
                }
                break;
            }
            default:
                return () => false;
        }

        return undefined;
    }

    static createOperatorFunction(
        sop: FilterBasicDataType | FilterConditionOperation,
        symbolId: string,
        symbolType: "number" | "string" | "date",
        symbolAccess: (symbolId: string, obj: any) => any,
    ): (obj: any) => boolean {
        const bIsNumber = symbolType == "number";
        const bIsString = symbolType == "string";
        const bIsDate = symbolType == "date";

        switch (true) {
            case _.isUndefined(sop):
                return (obj) => {
                    return _.isUndefined(symbolAccess(symbolId, obj) == undefined);
                };
            case _.isString(sop):
                return (obj) => {
                    return symbolAccess(symbolId, obj) == sop;
                };
            case _.isNumber(sop):
                return (obj) => {
                    return symbolAccess(symbolId, obj) == sop;
                };
            case _.isDate(sop):
                return (obj) => {
                    let dateVal = symbolAccess(symbolId, obj);
                    if (_.isDate(dateVal)) return dateVal.getTime() === sop.getTime();
                    return false;
                };
            default: {
                let subOp = FilterUtils.isOperatorObject(sop);
                if (subOp == false) {
                    // Unrecognized Operator Object
                    return () => false;
                }
                const subOpArgType = dctOpArgTypes[subOp];
                const subOpArg = (<any>sop)[subOp];
                let subOpArgDct: { [id: string]: any } = {};
                switch (subOpArgType) {
                    case "BasicDataType":
                        if (bIsDate && !_.isDate(subOpArg)) return () => false;
                        if (bIsNumber && !_.isNumber(subOpArg)) return () => false;
                        if (bIsString && !_.isString(subOpArg)) return () => false;
                        break;
                    case "BasicDataTypeArray":
                        if (!_.isArray(subOpArg)) return () => false;
                        // Could Validate More, Create Dictionary
                        switch (true) {
                            case bIsNumber:
                                break;
                            case bIsString:
                                subOpArgDct = _.keyBy(subOpArg, (i) => i);
                                break;
                            case bIsDate:
                                break;
                        }
                        break;
                    case "string":
                        if (!_.isString(subOpArg)) return () => false;
                        break;
                    default:
                        return () => false;
                }

                switch (subOp) {
                    case "inq":
                    case "in": {
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            return _.includes(subOpArg, val);
                        };
                    }

                    case "nin":
                    case "notIn":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            return !_.includes(subOpArg, val);
                        };

                    case "like":
                    case "contains":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            if (_.isString(val)) return val.includes(subOpArg);
                            return false;
                        };

                    case "nlike":
                    case "notContains":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            if (_.isString(val)) return !val.includes(subOpArg);
                            return true;
                        };
                    case "ilike":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            if (_.isString(val)) {
                                return val.toUpperCase().includes(_.upperCase(subOpArg));
                            }
                            return false;
                        };
                    case "nilike":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            if (_.isString(val)) return !val.toUpperCase().includes(_.upperCase(subOpArg));
                            return true;
                        };
                    case "gte":
                    case ">=":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            return val >= subOpArg;
                        };
                    case "gt":
                    case ">":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            return val > subOpArg;
                        };
                    case "lte":
                    case "<=":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            return val <= subOpArg;
                        };
                    case "lt":
                    case "<":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            return val < subOpArg;
                        };
                    case "neq":
                    case "!=":
                    case "<>":
                        return (obj) => {
                            const val = symbolAccess(symbolId, obj);
                            return val != subOpArg;
                        };

                    default:
                        // Unrecognized Operator
                        return () => false;
                }
            }
        }
    }
}
